// Global Instructions Rule Applied!
import { useEffect, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  addModuleTab,
  addFormTab,
  setActiveTab,
  removeTab,
  updateTabPath,
  selectTabs,
  selectActiveTab,
  selectActiveTabKey
} from '../../../store/slices/tabSlice'
import { store } from '../../../state_manager/store.ts'
import { TABLE_ACCOUNTS, TABLE_PROSPECTS } from '../../../state_manager/globals.ts'

/** What a URL asks the tab shell for: a record form, or a module's table / page tab. */
export type RouteInfo =
  | { type: 'form'; module: string; recordId: string | null; isNew: boolean }
  | { type: 'table' | 'page'; module: string }

/** Route → module aliases (the route segment differs from the module key). */
const ROUTE_MODULE_MAP: Record<string, string> = {
  deals: 'deals',
  companies: 'companies',
  contacts: 'contacts',
  fundraising: 'fundraising',
  dashboard: 'dashboard',
  lookups: 'lookups',
  operations: 'dashboard',
  'asset-transactions': 'asset-transactions',
  rounds: 'rounds',
  downloads: 'downloads',
  reports: 'reports',
  settings: 'lookups'
}

/** Module → table path where it is not simply `/${module}`. */
const MODULE_PATHS: Record<string, string> = {
  deals: '/deals',
  companies: '/companies',
  contacts: '/contacts',
  fundraising: '/fundraising',
  'fundraising-pipeline': '/fundraising/pipeline',
  dashboard: '/dashboard',
  'asset-transactions': '/asset-transactions',
  rounds: '/rounds'
}

/**
 * Tab Manager Hook
 *
 * Manages tab state integration with React Router
 * Automatically opens appropriate tabs based on current route
 * Handles tab navigation and synchronization
 */
export function useTabManager() {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const tabs = useSelector(selectTabs)
  const activeTab = useSelector(selectActiveTab)
  const activeTabKey = useSelector(selectActiveTabKey)

  // Track last path updates to prevent duplicate updateTabPath calls
  const lastPathUpdateRef = useRef(new Map<string, string>())
  // Track pending dispatches to prevent duplicate addFormTab calls
  const pendingDispatchesRef = useRef(new Set<string>())

  // Use refs to track current values to avoid dependency issues in useEffect
  const tabsRef = useRef(tabs)
  const activeTabKeyRef = useRef(activeTabKey)
  // Initialize to null to allow initial mount to process the URL
  const lastPathnameRef = useRef<string | null>(null)
  const isInitialMountRef = useRef(true)

  // Update refs when values change
  useEffect(() => {
    tabsRef.current = tabs
    activeTabKeyRef.current = activeTabKey
  }, [tabs, activeTabKey])

  // Parse current route to determine tab requirements
  const parseRoute = useCallback((pathname: string): RouteInfo => {
    // Handle form routes: /module/forms/full/recordId
    // Module segment allows '-' so hyphenated modules (e.g.
    // distribution-lists) get a full-form tab; existing single-word modules
    // are unaffected (backward-compatible superset).
    const fullFormMatch = pathname.match(/^\/([\w-]+)\/forms\/full\/(.+)$/)
    if (fullFormMatch) {
      const [, module, recordId] = fullFormMatch
      return {
        type: 'form',
        module,
        recordId: recordId === 'new' ? null : recordId,
        isNew: recordId === 'new'
      }
    }

    // Handle drawer routes: /module/forms/drawer/recordId (these should still show the table tab)
    // Special case for deal pipeline drawer
    if (pathname.match(/^\/deals\/pipeline\/forms\/drawer\/(.+)$/)) {
      return {
        type: 'table',
        module: 'deal-pipeline'
      }
    }

    // Special case for fundraising pipeline drawer
    if (pathname.match(/^\/fundraising\/pipeline\/forms\/drawer\/(.+)$/)) {
      return {
        type: 'table',
        module: 'fundraising-pipeline'
      }
    }

    // General drawer routes for other modules
    const drawerFormMatch = pathname.match(/^\/(\w+)\/forms\/drawer\/(.+)$/)
    if (drawerFormMatch) {
      const [, module] = drawerFormMatch
      return {
        type: 'table',
        module
      }
    }

    // Handle specific routes first
    if (pathname === '/deals/pipeline') {
      return {
        type: 'table',
        module: 'deal-pipeline'
      }
    }

    if (pathname === '/fundraising/pipeline') {
      return { type: 'table', module: 'fundraising-pipeline' }
    }

    if (pathname === '/downloads') {
      return { type: 'page', module: 'downloads' }
    }

    if (pathname === '/reports') {
      return { type: 'page', module: 'reports' }
    }

    // Handle module routes: /module
    const moduleMatch = pathname.match(/^\/([a-zA-Z0-9_-]+)(?:\/.*)?$/)
    if (moduleMatch) {
      const [, module] = moduleMatch

      return {
        type: 'table',
        module: ROUTE_MODULE_MAP[module] || module
      }
    }

    // Default to deal-pipeline
    return {
      type: 'table',
      module: 'deal-pipeline'
    }
  }, [])

  // Handle route changes and ensure appropriate tab is open
  useEffect(() => {
    // On initial mount, always process the URL (for direct navigation)
    const isInitialMount = isInitialMountRef.current
    if (isInitialMount) {
      isInitialMountRef.current = false
    }

    // Skip if the location hasn't actually changed (but allow initial mount).
    // The query string participates: new-record form tabs are keyed per query
    // (prefill context), so /forms/full/new?deal=A → ?deal=B must re-run even
    // though the pathname is identical.
    const locationKey = location.pathname + location.search
    if (!isInitialMount && lastPathnameRef.current === locationKey) {
      return
    }

    lastPathnameRef.current = locationKey

    // Don't interfere if we're in the middle of closing tabs (but allow initial mount)
    if (!isInitialMount && window.isClosingTab) {
      return
    }

    // Don't interfere if user is changing tabs (user-initiated change) (but allow initial mount)
    if (!isInitialMount && window.isUserChangingTab) {
      return
    }

    // Read current values from refs to avoid dependency issues
    const currentTabs = tabsRef.current
    const currentActiveTabKey = activeTabKeyRef.current

    const routeInfo = parseRoute(location.pathname)

    if (routeInfo.type === 'form') {
      // For form routes, ensure the form tab exists and is active. New-record
      // tabs include the query string in their key (see tabSlice.addFormTab)
      // so each prefill context gets its own tab.
      const formQuery = routeInfo.isNew && location.search ? location.search.replace(/^\?/, '') : ''
      const formQuerySuffix = formQuery ? `?${formQuery}` : ''
      const formTabKey = `${routeInfo.module}-form-${routeInfo.isNew ? 'new' : 'edit'}-${routeInfo.recordId || 'new'}${formQuerySuffix}`

      // Check if this tab already exists - avoid re-creating
      if (currentActiveTabKey === formTabKey) {
        return // Tab is already active, nothing to do
      }

      const existingFormTab = currentTabs.find((tab) => tab.key === formTabKey)

      if (!existingFormTab) {
        // Prevent duplicate dispatches
        const dispatchKey = `addFormTab-${formTabKey}`
        if (pendingDispatchesRef.current.has(dispatchKey)) {
          return
        }

        pendingDispatchesRef.current.add(dispatchKey)

        // Create new form tab
        dispatch(
          addFormTab({
            module: routeInfo.module,
            recordId: routeInfo.recordId,
            title: routeInfo.module.charAt(0).toUpperCase() + routeInfo.module.slice(1),
            isNew: routeInfo.isNew,
            query: formQuery
          })
        )

        // Clear the pending flag after dispatch
        setTimeout(() => {
          pendingDispatchesRef.current.delete(dispatchKey)
        }, 100)
      } else if (currentActiveTabKey !== formTabKey) {
        // Activate existing form tab
        dispatch(setActiveTab({ key: formTabKey }))
      }
    } else if (routeInfo.type === 'table' || routeInfo.type === 'page') {
      // For table/page routes, ensure the module tab exists and is active.
      // Form tabs share the same `module` as the grid; match only shell tabs (not forms).
      const existingModuleTab = currentTabs.find(
        (tab) =>
          tab.module === routeInfo.module && (tab.type === 'table' || tab.type === 'dashboard' || tab.type === 'page')
      )

      if (!existingModuleTab) {
        // Prevent duplicate dispatches
        const dispatchKey = `addModuleTab-${routeInfo.module}`
        if (pendingDispatchesRef.current.has(dispatchKey)) {
          return
        }

        pendingDispatchesRef.current.add(dispatchKey)

        // Create module tab only if it doesn't exist
        dispatch(addModuleTab({ module: routeInfo.module }))

        // Clear the pending flag after dispatch
        setTimeout(() => {
          pendingDispatchesRef.current.delete(dispatchKey)
        }, 100)
      } else {
        const moduleTabKey = existingModuleTab.key
        // Activate existing module tab
        if (currentActiveTabKey !== moduleTabKey) {
          dispatch(setActiveTab({ key: moduleTabKey }))
        }

        // Update tab path if it's different (e.g., /deals vs /deals/pipeline)
        // Only update if we haven't already set this path for this tab.
        //
        // Never store '/' as a tab's path. parseRoute maps '/' to the
        // deal-pipeline default tab, but '/' itself is RootLanding — a
        // transient redirect that unmounts the tab shell. If the default tab
        // remembered '/', closing the tab beside it would navigate there,
        // bounce through RootLanding to the area's default item, and
        // resurrect whatever tab that item belongs to (owner 2026-09-02).
        const lastPath = lastPathUpdateRef.current.get(moduleTabKey)
        if (
          location.pathname !== '/' &&
          existingModuleTab.path !== location.pathname &&
          lastPath !== location.pathname
        ) {
          const pathDispatchKey = `updateTabPath-${moduleTabKey}-${location.pathname}`
          if (pendingDispatchesRef.current.has(pathDispatchKey)) {
            return
          }

          pendingDispatchesRef.current.add(pathDispatchKey)
          lastPathUpdateRef.current.set(moduleTabKey, location.pathname)
          dispatch(updateTabPath({ key: moduleTabKey, path: location.pathname }))

          // Clear the pending flag after dispatch
          setTimeout(() => {
            pendingDispatchesRef.current.delete(pathDispatchKey)
          }, 100)
        }
      }
    }
  }, [location.pathname, location.search, dispatch, parseRoute])

  // Track pending navigation to prevent duplicate calls
  const pendingNavigationRef = useRef<string | null>(null)

  // Navigate to form in new tab. `query` (optional plain object) is appended
  // as a query string — forms read it once on mount as prefill for new
  // records (e.g. booking a transaction from a deal's Execution tab).
  const openFormInTab = useCallback(
    (
      module: string,
      recordId: string | number | null = null,
      // Accepted for callers' readability; the tab title is derived by the
      // route effect / MainTabContainer, not from this argument.
      _title: string | null = null,
      query: Record<string, unknown> | null = null
    ) => {
      const targetRecordId = recordId || 'new'

      if (module === TABLE_ACCOUNTS) {
        module = 'companies'
      }

      if (module === TABLE_PROSPECTS) {
        module = 'fundraising'
      }

      const queryEntries = Object.entries(query || {})
        .filter(([, v]) => v != null && v !== '')
        .map(([k, v]): [string, string] => [k, String(v)])
      const queryString = queryEntries.length ? `?${new URLSearchParams(queryEntries).toString()}` : ''
      const path = `/${module}/forms/full/${targetRecordId}${queryString}`

      // Prevent duplicate navigation calls. The query participates so two
      // quick opens with different prefill (e.g. booking from two deals)
      // both go through.
      const navigationKey = `${module}-${targetRecordId}${queryString}`
      if (pendingNavigationRef.current === navigationKey) {
        return // Already navigating to this path
      }

      pendingNavigationRef.current = navigationKey

      // Navigate to the form path, which will trigger tab creation
      navigate(path)

      // Clear the pending flag after a short delay to allow navigation to complete
      setTimeout(() => {
        if (pendingNavigationRef.current === navigationKey) {
          pendingNavigationRef.current = null
        }
      }, 500)
    },
    [navigate]
  )

  // Close a form tab (Cancel/Close buttons). A bare removeTab dispatch leaves
  // the URL parked on the closed form's path — re-opening the same form is
  // then a same-path navigation the route effect never sees (the button looks
  // dead), and a hard refresh resurrects the closed form. Remove the tab,
  // then re-anchor the URL on whichever tab became active.
  const closeFormTab = useCallback(
    (key: string) => {
      window.isClosingTab = true
      dispatch(removeTab({ key }))
      const next = store.getState().tabs
      const nextTab = next.tabs.find((t) => t.key === next.activeTabKey)
      if (nextTab?.path && nextTab.path !== location.pathname) {
        navigate(nextTab.path, { replace: true })
      }
      setTimeout(() => {
        window.isClosingTab = false
      }, 150)
    },
    [dispatch, navigate, location.pathname]
  )

  // Navigate to module table
  const openModuleTab = useCallback(
    (module: string) => {
      navigate(MODULE_PATHS[module] || `/${module}`)
    },
    [navigate]
  )

  return {
    // State
    tabs,
    activeTab,
    activeTabKey,

    // Actions
    openFormInTab,
    openModuleTab,
    closeFormTab,

    // Route parsing utility
    parseRoute
  }
}
